import { useMemo } from "react";
import { useDocuments } from "./useDocuments";
import { useQuizzes } from "./useQuizzes";
import {
  type DocumentSummary,
  type DocumentSummaryRelease,
  type IdAndTitle,
  type QuizSummary,
  type UserSummary,
} from "../types";

export interface UnanchoredDocumentRow {
  documentId: number;
  documentReleaseId: number;
  title: string;
  category: IdAndTitle | null;
  owner: UserSummary;
  publisher: UserSummary | null;
  publishedAt: string | null;
  adoptedAt: string | null;
  tags: string[];
}

/** The quiz `id` is the row id; the release fields win where both carry them. */
export type AnchoredDocumentRow = QuizSummary & UnanchoredDocumentRow;

const toUnanchoredRow = (
  document: DocumentSummary,
  release: DocumentSummaryRelease,
): UnanchoredDocumentRow => ({
  documentId: document.id,
  documentReleaseId: release.id,
  title: release.artifact.title,
  category: document.category,
  owner: document.owner,
  publisher: document.publisher,
  publishedAt: release.artifact.publishedAt,
  adoptedAt: release.adoptedAt,
  tags: document.tags.map((tag) => tag.title),
});

/**
 * Published documents split into those that have a quiz (anchored) and those
 * that do not (unanchored). Each row represents a document's latest published
 * version (matching the library). Quiz-side data comes from `useQuizzes`.
 */
export function useAnchoringDocuments() {
  const documents = useDocuments({ includeArchived: false });
  const quizzes = useQuizzes();

  const { anchored, unanchored } = useMemo(() => {
    const anchoredRows: AnchoredDocumentRow[] = [];
    const unanchoredRows: UnanchoredDocumentRow[] = [];

    if (!documents.data || !quizzes.data) {
      return { anchored: anchoredRows, unanchored: unanchoredRows };
    }

    const quizByReleaseId = new Map(
      quizzes.data.map((quiz) => [quiz.documentReleaseId, quiz]),
    );

    for (const document of documents.data) {
      const release = document.latestPublishedRelease;
      if (!release) continue;

      const row = toUnanchoredRow(document, release);
      const quiz = quizByReleaseId.get(release.id);

      if (quiz) {
        anchoredRows.push({ ...quiz, ...row });
      } else {
        unanchoredRows.push(row);
      }
    }

    unanchoredRows.sort((a, b) => a.title.localeCompare(b.title));

    return { anchored: anchoredRows, unanchored: unanchoredRows };
  }, [documents.data, quizzes.data]);

  return {
    anchored,
    unanchored,
    isLoading: documents.isLoading || quizzes.isLoading,
    error: documents.error ?? quizzes.error,
    mutate: quizzes.mutate,
  };
}
